import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  Tag, 
  Plus, 
  Trash2, 
  ChevronDown,
  ChevronRight,
  Edit3,
  Eye,
  EyeOff,
  MapPin,
  Palette
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

// Predefined tag colors
const TAG_COLORS = [
  { name: 'Blue', value: { r: 0, g: 0.5, b: 1 }, hex: '#0080FF' },
  { name: 'Teal', value: { r: 0, g: 0.8, b: 0.8 }, hex: '#00CCCC' },
  { name: 'Green', value: { r: 0.2, g: 0.8, b: 0.2 }, hex: '#33CC33' },
  { name: 'Yellow', value: { r: 1, g: 0.8, b: 0 }, hex: '#FFCC00' },
  { name: 'Orange', value: { r: 1, g: 0.5, b: 0 }, hex: '#FF8000' },
  { name: 'Red', value: { r: 1, g: 0.2, b: 0.2 }, hex: '#FF3333' },
  { name: 'Purple', value: { r: 0.6, g: 0.2, b: 0.8 }, hex: '#9933CC' },
  { name: 'Pink', value: { r: 1, g: 0.4, b: 0.6 }, hex: '#FF6699' },
  { name: 'Gold', value: { r: 0.8, g: 0.6, b: 0.2 }, hex: '#CC9933' },
];

export function TagManager({ 
  tags = [], 
  customTags = [],
  cameraPosition,
  onCreateTag,
  onDeleteTag,
  onNavigateToTag,
  sdkConnected = false,
  isTagPlacementMode = false,
  onStartTagPlacement,
  onCancelTagPlacement,
  onPlaceTag,
  pointerIntersection,
}) {
  const { canEdit, isViewer } = useAuth();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isModelTagsOpen, setIsModelTagsOpen] = useState(true);
  const [isCustomTagsOpen, setIsCustomTagsOpen] = useState(true);
  const [newTag, setNewTag] = useState({
    label: '',
    description: '',
    color: TAG_COLORS[8], // Default to Gold (Zentara brand)
  });

  const allTags = [...tags, ...customTags];

  // Start placement mode - user will click on model to place
  const handleStartPlacement = () => {
    if (newTag.label && onStartTagPlacement) {
      onStartTagPlacement({
        label: newTag.label,
        description: newTag.description,
        color: newTag.color.value,
      });
      setIsCreateDialogOpen(false);
    }
  };

  // Handle tag placed callback
  const handleTagPlaced = (tagId, position) => {
    console.log('Tag placed:', tagId, position);
    setNewTag({
      label: '',
      description: '',
      color: TAG_COLORS[8],
    });
  };

  const getTagColor = (tag) => {
    if (tag.color) {
      if (typeof tag.color === 'string') return tag.color;
      return `rgb(${Math.round(tag.color.r * 255)}, ${Math.round(tag.color.g * 255)}, ${Math.round(tag.color.b * 255)})`;
    }
    return 'hsl(var(--primary))';
  };

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Tags & Annotations</span>
          <Badge variant="secondary" className="text-[10px]">
            {allTags.length}
          </Badge>
        </div>
        
        {canEdit && (
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 gap-1">
                <Plus className="h-3 w-3" />
                Add Tag
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Tag</DialogTitle>
              <DialogDescription>
                Add an annotation or marker to a specific point in the 3D space.
                {!sdkConnected && (
                  <span className="block mt-1 text-warning">
                    Note: SDK not fully connected. Tags may not persist.
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="tag-label">Tag Label *</Label>
                <Input
                  id="tag-label"
                  placeholder="e.g., Main Valve, Tank A, Equipment ID..."
                  value={newTag.label}
                  onChange={(e) => setNewTag(prev => ({ ...prev, label: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tag-desc">Description</Label>
                <Textarea
                  id="tag-desc"
                  placeholder="Additional details about this tag..."
                  value={newTag.description}
                  onChange={(e) => setNewTag(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Tag Color</Label>
                <div className="flex flex-wrap gap-2">
                  {TAG_COLORS.map((color) => (
                    <button
                      key={color.name}
                      className={cn(
                        "w-8 h-8 rounded-lg border-2 transition-all",
                        newTag.color.name === color.name 
                          ? "border-foreground scale-110" 
                          : "border-transparent hover:scale-105"
                      )}
                      style={{ backgroundColor: color.hex }}
                      onClick={() => setNewTag(prev => ({ ...prev, color }))}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>

              <div className="bg-secondary/50 p-3 rounded-lg space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <MapPin className="h-4 w-4 text-primary" />
                  Click to Place
                </div>
                <p className="text-xs text-muted-foreground">
                  After clicking &quot;Place Tag&quot;, click on any surface in the 3D model 
                  (wall, floor, equipment) to place the tag at that exact location.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleStartPlacement} 
                disabled={!newTag.label || !sdkConnected}
                className="gap-1"
              >
                <MapPin className="h-3 w-3" />
                Place Tag
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Tag Placement Mode Indicator */}
      {isTagPlacementMode && (
        <div className="bg-primary/20 border border-primary p-3 rounded-lg space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-primary">
              <MapPin className="h-4 w-4 animate-pulse" />
              Placement Mode Active
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onCancelTagPlacement}
              className="h-6 text-xs"
            >
              Cancel
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Click on any surface in the 3D model to place your tag.
          </p>
          {pointerIntersection && (
            <p className="text-xs text-muted-foreground">
              Target: ({pointerIntersection.position?.x?.toFixed(2)}, {pointerIntersection.position?.y?.toFixed(2)}, {pointerIntersection.position?.z?.toFixed(2)})
            </p>
          )}
        </div>
      )}

      {/* Viewer mode notice */}
      {isViewer && (
        <div className="text-xs text-muted-foreground bg-secondary/50 p-2 rounded">
          View-only access. Contact an admin to create or edit tags.
        </div>
      )}

      {/* Model Tags Section */}
      {tags.length > 0 && (
        <Collapsible open={isModelTagsOpen} onOpenChange={setIsModelTagsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-2 h-auto">
              <span className="text-xs font-medium text-muted-foreground">
                Model Tags ({tags.length})
              </span>
              {isModelTagsOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-1">
            {tags.map((tag) => (
              <TagItem
                key={tag.id}
                tag={tag}
                onNavigate={() => onNavigateToTag && onNavigateToTag(tag.id)}
                onDelete={canEdit ? () => onDeleteTag && onDeleteTag(tag.id, tag.dbId) : null}
                getTagColor={getTagColor}
                canEdit={canEdit}
              />
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Custom Tags Section */}
      {customTags.length > 0 && (
        <Collapsible open={isCustomTagsOpen} onOpenChange={setIsCustomTagsOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-2 h-auto">
              <span className="text-xs font-medium text-muted-foreground">
                Custom Tags ({customTags.length})
              </span>
              {isCustomTagsOpen ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-1">
            {customTags.map((tag) => (
              <TagItem
                key={tag.id}
                tag={tag}
                onNavigate={() => onNavigateToTag && onNavigateToTag(tag.id)}
                onDelete={() => onDeleteTag && onDeleteTag(tag.id)}
                getTagColor={getTagColor}
                isCustom
                canEdit={canEdit}
              />
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Empty State */}
      {allTags.length === 0 && (
        <div className="text-center py-6">
          <Tag className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No tags yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click Add Tag to create your first annotation
          </p>
        </div>
      )}
    </div>
  );
}

// Individual tag item component with expandable details
function TagItem({ tag, onNavigate, onDelete, getTagColor, isCustom = false, canEdit = false }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="rounded-lg bg-secondary/50 hover:bg-secondary/80 transition-colors overflow-hidden">
      <div 
        className="flex items-center gap-3 p-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div 
          className="w-4 h-4 rounded-full flex-shrink-0 shadow-sm"
          style={{ backgroundColor: getTagColor(tag) }}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{tag.label}</p>
        </div>
        <div className="flex items-center gap-1">
          {onNavigate && (
            <Button 
              variant="ghost" 
              size="icon-sm" 
              onClick={(e) => { e.stopPropagation(); onNavigate(); }} 
              title="Navigate to tag"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
          {isCustom && (
            <Badge variant="outline" className="text-[9px] px-1">Custom</Badge>
          )}
          <ChevronDown className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            expanded && "rotate-180"
          )} />
        </div>
      </div>
      
      {expanded && (
        <div className="px-3 pb-3 pt-0 border-t border-border/50">
          {tag.description ? (
            <p className="text-xs text-muted-foreground mt-2">{tag.description}</p>
          ) : (
            <p className="text-xs text-muted-foreground/50 mt-2 italic">No description</p>
          )}
          
          {tag.position && (
            <div className="mt-2 text-xs text-muted-foreground">
              <span className="font-mono bg-muted px-1 rounded">
                X: {tag.position.x?.toFixed(1)}, Y: {tag.position.y?.toFixed(1)}, Z: {tag.position.z?.toFixed(1)}
              </span>
            </div>
          )}
          
          <div className="flex items-center gap-2 mt-3">
            {onNavigate && (
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1 h-8"
                onClick={onNavigate}
              >
                <Eye className="h-3 w-3 mr-1" />
                Go to Tag
              </Button>
            )}
            {onDelete && canEdit && (
              <Button 
                variant="destructive" 
                size="sm"
                className="h-8"
                onClick={onDelete}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TagManager;
